/**
 * Platform admin -- LLM cost dashboard.
 *
 * Cross-tenant rollups from costTracker.getDailyTotals(). Today + yesterday
 * + month-to-date with per-provider + top-tenant breakdown so a founder can
 * see where the spend is going at a glance. Soft-cap crossings surfaced so
 * the operator can email tenants before they hit the hard cap.
 */

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Activity, AlertTriangle, ArrowDown, ArrowUp, DollarSign, Layers, TrendingUp, Users } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface ProviderRow {
  provider: string;
  spendUSD: number;
  calls: number;
}
interface TenantRow {
  tenantId: string;
  tenant: { id: string; name: string; slug: string; plan: string } | null;
  spendUSD: number;
  calls: number;
}
interface DailyTotals {
  day: string;
  totalSpendUSD: number;
  totalCalls: number;
  tokensIn: number;
  tokensOut: number;
  byProvider: ProviderRow[];
  topTenants: TenantRow[];
}
interface SoftCapCrossing {
  tenantId: string;
  spendUSD: number;
  capUSD: number;
  utilisation: number;
}
interface CostResp {
  today: DailyTotals;
  yesterday: DailyTotals;
  monthToDate: { totalSpendUSD: number; totalCalls: number; tokensIn: number; tokensOut: number; period: string };
  softCapCrossings: SoftCapCrossing[];
}

export function PlatformCostPage() {
  const cost = useQuery({
    queryKey: ['platform-admin', 'cost'],
    queryFn: async () => (await api.get<CostResp>('/api/platform-admin/cost')).data,
    refetchInterval: 60_000,
  });

  if (cost.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-3 md:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
      </div>
    );
  }
  if (!cost.data) return null;

  const todayUSD = cost.data.today.totalSpendUSD;
  const yesterdayUSD = cost.data.yesterday.totalSpendUSD;
  const dod = yesterdayUSD === 0 ? null : (todayUSD - yesterdayUSD) / yesterdayUSD;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl">LLM cost dashboard</h1>
        <p className="text-sm text-muted-foreground">
          Cross-tenant LLM spend rollups. Refreshes every 60 seconds. Per-tenant detail under
          tenants {' -> '} pick one {' -> '} usage tab.
        </p>
      </div>

      {/* Headline cards */}
      <div className="grid gap-3 md:grid-cols-4">
        <KpiCard
          icon={DollarSign}
          label="Today"
          value={formatUsd(todayUSD)}
          sub={dod != null ? `${dod >= 0 ? '+' : ''}${(dod * 100).toFixed(0)}% vs yesterday` : undefined}
          tone={dod != null && dod > 0.5 ? 'warning' : 'default'}
          trend={dod}
        />
        <KpiCard
          icon={Activity}
          label="Yesterday"
          value={formatUsd(yesterdayUSD)}
          sub={`${cost.data.yesterday.totalCalls.toLocaleString()} calls`}
        />
        <KpiCard
          icon={TrendingUp}
          label={`Month-to-date (${cost.data.monthToDate.period})`}
          value={formatUsd(cost.data.monthToDate.totalSpendUSD)}
          sub={`${cost.data.monthToDate.totalCalls.toLocaleString()} calls`}
        />
        <KpiCard
          icon={Layers}
          label="Tokens today"
          value={`${(cost.data.today.tokensIn + cost.data.today.tokensOut).toLocaleString()}`}
          sub={`${cost.data.today.tokensIn.toLocaleString()} in / ${cost.data.today.tokensOut.toLocaleString()} out`}
        />
      </div>

      {/* Soft-cap crossings */}
      {cost.data.softCapCrossings.length > 0 && (
        <Card className="border-amber-300/60 bg-amber-50/40 dark:border-amber-900/40 dark:bg-amber-950/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base text-amber-900 dark:text-amber-300">
              <AlertTriangle className="h-4 w-4" />
              {cost.data.softCapCrossings.length} tenant{cost.data.softCapCrossings.length === 1 ? '' : 's'} crossed soft cap (not yet notified this month)
            </CardTitle>
            <CardDescription>
              The cost-monitor worker emails tenant ADMINs at the soft-cap threshold; these tenants are queued
              for notification on the next worker tick.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1 text-xs">
              {cost.data.softCapCrossings.map((c) => (
                <li key={c.tenantId} className="flex items-center justify-between rounded-md bg-white/40 p-2 dark:bg-white/5">
                  <Link to={`/platform-admin/tenants/${c.tenantId}`} className="font-mono hover:underline">
                    {c.tenantId.slice(0, 8)}
                  </Link>
                  <span>
                    ${c.spendUSD.toFixed(2)} / ${c.capUSD.toFixed(0)}
                    <span className="ml-2 font-medium">({(c.utilisation * 100).toFixed(0)}%)</span>
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Per-provider breakdown today */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Today by provider</CardTitle>
            <CardDescription>Where today's LLM spend went.</CardDescription>
          </CardHeader>
          <CardContent>
            {cost.data.today.byProvider.length === 0 ? (
              <p className="text-xs text-muted-foreground">No calls today yet.</p>
            ) : (
              <ul className="space-y-2">
                {cost.data.today.byProvider.map((p) => {
                  const share = todayUSD === 0 ? 0 : p.spendUSD / todayUSD;
                  return (
                    <li key={p.provider}>
                      <div className="flex items-baseline justify-between text-xs">
                        <span className="font-medium">{p.provider}</span>
                        <span>
                          {formatUsd(p.spendUSD)} <span className="text-muted-foreground">({p.calls} calls)</span>
                        </span>
                      </div>
                      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted">
                        <div className="h-full bg-primary" style={{ width: `${(share * 100).toFixed(1)}%` }} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4" /> Top tenants today
            </CardTitle>
            <CardDescription>Highest-spending tenants in the last 24h.</CardDescription>
          </CardHeader>
          <CardContent>
            {cost.data.today.topTenants.length === 0 ? (
              <p className="text-xs text-muted-foreground">No tenants spent today.</p>
            ) : (
              <ul className="space-y-1">
                {cost.data.today.topTenants.map((t) => (
                  <li
                    key={t.tenantId}
                    className="flex items-center justify-between rounded-md border p-2 text-xs"
                  >
                    <div className="min-w-0 flex-1">
                      <Link
                        to={`/platform-admin/tenants/${t.tenantId}`}
                        className="font-medium hover:underline"
                      >
                        {t.tenant?.name ?? t.tenantId.slice(0, 8)}
                      </Link>
                      <div className="text-[10px] text-muted-foreground">
                        {t.tenant?.plan ?? '-'} -- {t.calls.toLocaleString()} calls
                      </div>
                    </div>
                    <Badge variant="secondary">{formatUsd(t.spendUSD)}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <p className="text-xs text-muted-foreground">
        Soft-cap notifications are sent automatically by the cost-monitor worker (Bull queue). Hard-cap
        breach refuses LLM calls with HTTP 402; per-tenant status is at <code>/settings/usage</code>.
      </p>
    </div>
  );
}

function KpiCard({
  icon: Icon,
  label,
  value,
  sub,
  tone = 'default',
  trend,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  sub?: string;
  tone?: 'default' | 'warning' | 'destructive';
  trend?: number | null;
}) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary">
            <Icon className="h-4 w-4" />
          </div>
          {trend != null && (
            <span
              className={cn(
                'inline-flex items-center gap-0.5 text-[10px] font-medium',
                trend > 0 ? 'text-amber-600' : 'text-emerald-600',
              )}
            >
              {trend > 0 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
              {Math.abs(trend * 100).toFixed(0)}%
            </span>
          )}
        </div>
        <div
          className={cn(
            'mt-4 font-display text-2xl',
            tone === 'destructive' && 'text-destructive',
            tone === 'warning' && 'text-amber-600',
          )}
        >
          {value}
        </div>
        <div className="text-xs text-muted-foreground">{label}</div>
        {sub && <div className="mt-0.5 text-[10px] text-muted-foreground">{sub}</div>}
      </CardContent>
    </Card>
  );
}

function formatUsd(n: number): string {
  if (n < 0.01) return `$${n.toFixed(4)}`;
  if (n < 100) return `$${n.toFixed(2)}`;
  return `$${Math.round(n).toLocaleString()}`;
}
